import { DateTime } from 'luxon';
import {
  Severity, SignalTextPart, STORED_REVIEW_PAGES, StoredPersonWeek, StoredPersonWeekDayIssue, StoredPersonWeekSignal, Signal
} from '../models';
import { PreviewButton, PreviewCardCopy } from './person-card-preview-copy';
import {
  DayState, PersonWeekDay, PersonWeekDayValue, PersonWeekEvidence, companyChartWeekdays, elapsedChartWeekdays
} from './person-week-evidence-detail';
import { collectPersonWeekSignalIds, pickPersonWeekAnalysis } from './ask-ai-mock-data';
import { joinWithAnd } from './ask-ai-signal-body';
import { chooseSecondaryHighlight } from './choose-secondary-highlight';
import {
  buildPersonCardSignalLink, CardSeverity, FiredSignal, formatSignalValue, PersonCard, PERSON_SIGNAL_TYPES,
  PersonSignalType, resolveSignalLink, RenderedBody, signalTypeLabel
} from './person-signal-card';

/**
 * AA-247 — adapt the real precomputed person-week cards (GET activity/agent-gateway/signals,
 * `personWeeks`) into the shapes Iulian's Needs you card design already renders:
 *   - PersonWeekPreviewCard  → the card itself (his PreviewCard = PersonCard + authored copy).
 *   - PersonWeekEvidence     → the per-user "View details" week matrix.
 *   - Signal                 → a live-analysis handle so the ✦ follow-up opens a real conversation.
 *
 * Presentation/adapter only: severity, ordering and CTAs come straight from the stored card, so
 * nothing here re-evaluates thresholds. The one derived-but-approximate piece is each signal's
 * x/y band distances (View details' per-day chip tint), which the response doesn't carry — see
 * deriveBandDistances.
 */

/** One thin bar in the card's inline daily strip — one per fired signal type, per weekday. */
export interface PreviewWeekBar {
  label: string;
  breaching: boolean;
  isPeak: boolean;
  displayValue: string;
}

export interface PreviewWeekDay {
  day: string;
  bars: PreviewWeekBar[];
}

/** His card shape: the generated PersonCard with authored copy overlaid (person-card-preview-copy.ts),
 * plus the inline daily strip and the week end date (for the provenance line) from the stored week. */
export type PersonWeekPreviewCard = PersonCard & {
  copy: PreviewCardCopy;
  weekChart: PreviewWeekDay[];
  weekStart: string;
  weekEnd: string;
};

const WEEKDAY_ORDER = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

/** The five person-signal types the card design knows about; anything else is ignored. */
function isPersonSignalType(type: string): type is PersonSignalType {
  return (PERSON_SIGNAL_TYPES as readonly string[]).includes(type);
}

/** 'critical'|'warning'|'info' — never the 'ai' pin severity, which a person-week never carries. */
function toCardSeverity(severity: Severity | undefined): CardSeverity {
  return severity === 'critical' || severity === 'warning' ? severity : 'info';
}

/** Weekday label ('Mon'..'Sun') for an ISO date, read positionally so it never drifts by zone. */
function weekdayLabel(isoDate: string): string {
  const day = DateTime.fromISO(isoDate, { zone: 'utc' });
  return day.isValid ? WEEKDAY_ORDER[day.weekday - 1] : '';
}

const SEVERITY_RANK: Record<CardSeverity, number> = { info: 0, warning: 1, critical: 2 };

/** Only the five known types, severity desc then days-over desc — same tie-break as the card. */
function knownSignals(week: StoredPersonWeek): StoredPersonWeekSignal[] {
  return week.signals
    .filter(s => isPersonSignalType(s.type))
    .slice()
    .sort((a, b) => {
      const sevDiff = SEVERITY_RANK[toCardSeverity(b.severity)] - SEVERITY_RANK[toCardSeverity(a.severity)];
      if (sevDiff !== 0) return sevDiff;
      return b.daysOverLimit - a.daysOverLimit;
    });
}

/** Native-unit label for a signal's peak, preferring the backend's own display string. */
function peakLabel(signal: StoredPersonWeekSignal): string {
  if (signal.peakValue.display) return signal.peakValue.display;
  return isPersonSignalType(signal.type) ? formatSignalValue(signal.type, signal.peakValue.value) : `${signal.peakValue.value}`;
}

/** Green evidence chip — a SignalTextPart with `link` set renders as an app-pds-chip citation pill
 * (open_in_new glyph). `url` is the resolved report href (deepLink when usable, else client fallback). */
function evidenceChip(label: string, url = ''): SignalTextPart {
  return url ? { link: label, url } : { link: label };
}

/**
 * Href for a body evidence chip. Prefer gateway deepLink; rebuild via PERSON_SIGNAL_REPORT_ROUTE when
 * deepLink is missing or unusable (e.g. manual_time → /approvals).
 */
function chipHref(signal: StoredPersonWeekSignal, userId: string): string {
  const type = isPersonSignalType(signal.type) ? signal.type : undefined;
  const fallback = type && signal.peakDay
    ? buildPersonCardSignalLink(type, userId, new Date(`${signal.peakDay}T00:00:00Z`))
    : '';
  return resolveSignalLink({ deepLink: signal.deepLink, type }, fallback) ?? '';
}

/** Peak used by the company chart week (start day + four). A gateway peak on either weekend day
 * is replaced by the highest fired chart day, so the tall bar, the chip, and View details never
 * cite a day the chart does not draw. */
function chartPeak(
  signal: StoredPersonWeekSignal, week: StoredPersonWeek, firstDayOfWeek = 1,
): { weekday: string, value: number, display: string } | undefined {
  const chartDays = companyChartWeekdays(firstDayOfWeek);
  const stored = weekdayLabel(signal.peakDay);
  if (chartDays.includes(stored)) {
    return { weekday: stored, value: signal.peakValue.value, display: peakLabel(signal) };
  }
  let bestDate = '';
  let bestValue = Number.NEGATIVE_INFINITY;
  let bestDisplay = '';
  for (const date of signal.firedDates) {
    const weekday = weekdayLabel(date);
    if (!chartDays.includes(weekday)) continue;
    const day = week.days.find(d => d.date === date);
    const issue = day?.issues.find(i => i.type === signal.type);
    const value = issueValue(signal.type as PersonSignalType, issue);
    const numeric = value ?? Number.NEGATIVE_INFINITY;
    if (!bestDate || numeric > bestValue || (numeric === bestValue && date < bestDate)) {
      bestDate = date;
      bestValue = numeric;
      bestDisplay = issue?.value.display?.trim() || '';
    }
  }
  if (!bestDate || bestValue === Number.NEGATIVE_INFINITY) return undefined;
  return { weekday: weekdayLabel(bestDate), value: bestValue, display: bestDisplay || peakLabel(signal) };
}

function chipLabel(signal: StoredPersonWeekSignal, week: StoredPersonWeek, firstDayOfWeek = 1): string {
  const peak = chartPeak(signal, week, firstDayOfWeek);
  if (!peak) return peakLabel(signal);
  return `${peak.display} on ${peak.weekday}`;
}

function escapeRe(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Regex that finds where a signal's peak value is stated in the Gemini prose. The model's wording
 * varies ('10h' vs '10 hours', '62%' vs '62 percent', '2h 15m' vs '2 hours 15 minutes'), so besides
 * the literal display string we also accept the spelled-out unit forms. Case-insensitive, first
 * occurrence — used to splice the chip in place of that span (see weaveEvidence). Only ever built
 * from the peak value, never the limit, so a sentence's threshold ('over your 40% limit') is safe.
 */
function peakValueMatcher(signal: StoredPersonWeekSignal): RegExp {
  const display = peakLabel(signal).trim();
  const alts = new Set<string>();
  if (display) alts.add(escapeRe(display).replace(/\s+/g, '\\s+'));
  const duration = display.match(/^([\d.]+)\s*h\s*([\d.]+)\s*m$/i);
  const hours = display.match(/^([\d.]+)\s*h$/i);
  const minutes = display.match(/^([\d.]+)\s*m$/i);
  const percent = display.match(/^([\d.]+)\s*%$/);
  if (duration) {
    alts.add(`${escapeRe(duration[1])}\\s*(?:h|hr|hrs|hour|hours)\\s*(?:and\\s*)?` +
      `${escapeRe(duration[2])}\\s*(?:m|min|mins|minute|minutes)\\b`);
  } else if (hours) {
    alts.add(`${escapeRe(hours[1])}\\s*(?:h|hr|hrs|hour|hours)\\b`);
  } else if (minutes) {
    alts.add(`${escapeRe(minutes[1])}\\s*(?:m|min|mins|minute|minutes)\\b`);
  } else if (percent) {
    alts.add(`${escapeRe(percent[1])}\\s*(?:%|percent|per\\s*cent)`);
  }
  return new RegExp(`(?:${[...alts].join('|')})`, 'i');
}

/** Matches a trailing " on <day>" right after the value — the peak's weekday (short or long) or its
 * ISO date — so "62% on Wed" / "55% on 2026-09-23" fold the day into the chip; otherwise it's left
 * in the sentence (e.g. "3h 20m of manual time on Tue" keeps a bare "3h 20m" chip, like AA-238). */
function trailingDayMatcher(signal: StoredPersonWeekSignal): RegExp {
  const day = DateTime.fromISO(signal.peakDay, { zone: 'utc' });
  const alts = [weekdayLabel(signal.peakDay), day.isValid ? day.toFormat('cccc') : '', signal.peakDay]
    .filter(Boolean)
    .map(escapeRe);
  return new RegExp(`^\\s+on\\s+(?:${alts.join('|')})\\b`, 'i');
}

interface ChipDescriptor {
  label: string;
  url: string;
  value: RegExp;
  day: RegExp;
}

interface WeaveHit {
  start: number;
  end: number;
  descriptor: ChipDescriptor;
}

/** First place `descriptor` matches at/after `from`, extended to swallow a trailing weekday. */
function findChipHit(text: string, from: number, descriptor: ChipDescriptor): Omit<WeaveHit, 'descriptor'> | null {
  const match = descriptor.value.exec(text.slice(from));
  if (!match) return null;
  const start = from + match.index;
  let end = start + match[0].length;
  const tail = descriptor.day.exec(text.slice(end));
  if (tail && tail.index === 0) end += tail[0].length;
  return { start, end };
}

/**
 * Weave each signal's peak chip INTO the prose, replacing the span where its value is stated (so a
 * sentence reads "peaking at [62% on Wed]" — AA-238's design) rather than appending pills below.
 * Each value is spliced at most once (left-to-right, one chip per signal); a signal whose value
 * can't be confidently located is returned unwoven for the caller to append, so no evidence is
 * dropped and nothing is rendered twice.
 */
function weaveEvidence(
  text: string, descriptors: ChipDescriptor[],
): { parts: SignalTextPart[], woven: Set<ChipDescriptor> } {
  const parts: SignalTextPart[] = [];
  const woven = new Set<ChipDescriptor>();
  let cursor = 0;
  for (;;) {
    let best: WeaveHit | null = null;
    for (const descriptor of descriptors) {
      if (woven.has(descriptor)) continue;
      const hit = findChipHit(text, cursor, descriptor);
      if (hit && (!best || hit.start < best.start)) best = { ...hit, descriptor };
    }
    if (!best) break;
    if (best.start > cursor) parts.push({ text: text.slice(cursor, best.start) });
    parts.push(evidenceChip(best.descriptor.label, best.descriptor.url));
    woven.add(best.descriptor);
    cursor = best.end;
  }
  if (cursor < text.length) parts.push({ text: text.slice(cursor) });
  return { parts, woven };
}

/** Push chips after the prose, separated by a paragraph break (the pre-inline fallback layout). */
function appendChips(into: SignalTextPart[], chips: SignalTextPart[]): void {
  if (!chips.length) return;
  if (into.length) into.push({ break: true });
  chips.forEach(chip => into.push(chip));
}

/**
 * Card body. For analyzed cards (a Gemini write-up is ready) the fired-day evidence is woven inline
 * as green chips where the prose states each peak — AA-238's design. Any peak the wording doesn't
 * clearly contain falls back to an appended chip so no evidence is lost. Unanalyzed cards keep the
 * plain prose with all chips appended below (unchanged).
 */
export function buildPreviewBody(week: StoredPersonWeek, firstDayOfWeek = 1): SignalTextPart[] {
  const text = week.body.trim();
  const fired = knownSignals(week).filter(s => s.peakDay);
  const preAnalyzed = week.analysisStatus === 'ready';

  if (!preAnalyzed || !text) {
    const plain: SignalTextPart[] = [];
    if (text) plain.push({ text });
    appendChips(plain, fired.map(s => evidenceChip(chipLabel(s, week, firstDayOfWeek), chipHref(s, week.userId))));
    return plain;
  }

  const descriptors: ChipDescriptor[] = fired.map(s => ({
    label: chipLabel(s, week, firstDayOfWeek),
    url: chipHref(s, week.userId),
    value: peakValueMatcher(s),
    day: trailingDayMatcher(s),
  }));
  const { parts, woven } = weaveEvidence(text, descriptors);
  appendChips(parts, descriptors.filter(d => !woven.has(d)).map(d => evidenceChip(d.label, d.url)));
  return parts;
}

const PREP_BUTTON_LABEL = 'PREP 1:1';

/** Same button as before. Old cards stored PREP A 1:1 or the PREP_A_1_1 key. */
function isPrepCta(cta: { key?: string, label?: string }): boolean {
  const key = cta.key ?? '';
  if (key === 'PREP_A_1_1' || key === 'PREP_A_1ON1') return true;
  const label = (cta.label ?? '').trim();
  return /^prep a 1:1$/i.test(label) || /^prep 1:1$/i.test(label);
}

/** Copy-modal CTAs stay on the card. Draft a message has no real page. Prep 1:1 uses that same popup. */
function isCopyModalCta(cta: { key?: string, href?: string, label?: string }): boolean {
  if (cta.key === 'DRAFT_A_MESSAGE' || (cta.href ?? '').startsWith('/messages/draft')) return true;
  return isPrepCta(cta);
}

/** Saved review page. Empty when the card was written before `page` existed. */
function savedReviewHref(cta: { href?: string, page?: string }): string {
  const page = cta.page?.trim() ?? '';
  if (!(STORED_REVIEW_PAGES as readonly string[]).includes(page)) return '';
  const path = `/${page}`;
  const href = cta.href?.trim() ?? '';
  if (href === path || href.startsWith(`${path};`) || href.startsWith(`${path}?`)) return href;
  return path;
}

/** [primary, secondary?] as his PreviewButton shape. A no-href CTA (ANALYZE) keeps its in-app behaviour. */
function buildButtons(week: StoredPersonWeek): PreviewButton[] {
  const buttons: PreviewButton[] = [];
  // Cards written before `page` still open the lead signal. A saved page wins.
  const lead = knownSignals(week)[0];
  const leadHref = lead ? chipHref(lead, week.userId) : '';

  const toButton = (
    cta: { key?: string, label: string, href: string, page?: string } | null | undefined,
  ): PreviewButton | null => {
    if (!cta || !cta.label) return null;
    if (isCopyModalCta(cta)) {
      return { label: isPrepCta(cta) ? PREP_BUTTON_LABEL : cta.label, draft: true };
    }
    const isReviewEvidence =
      cta.key === 'REVIEW_EVIDENCE' || /^review\s+evidence$/i.test(cta.label.trim());
    const saved = isReviewEvidence ? savedReviewHref(cta) : '';
    const target = saved || (isReviewEvidence ? leadHref : '') || cta.href?.trim() || '';
    return target ? { label: cta.label, href: target } : { label: cta.label };
  };
  const primary = toButton(week.primaryCta);
  if (primary) buttons.push(primary);
  if (week.analysisStatus === 'ready') {
    const highlight = chooseSecondaryHighlight(week);
    buttons.push({ label: highlight.label, href: highlight.href });
  } else {
    const secondary = toButton(week.secondaryCta);
    if (secondary) buttons.push(secondary);
  }
  if (!buttons.length) buttons.push({ label: 'ANALYZE' });
  return buttons;
}

/** ✦ follow-up prompt for an analyzed card — leads with the top signal's peak day, from real data. */
export function followUpQuestion(week: StoredPersonWeek, firstDayOfWeek = 1): string {
  const lead = knownSignals(week)[0];
  const day = lead ? (chartPeak(lead, week, firstDayOfWeek)?.weekday ?? '') : '';
  return day ? `What happened on ${day}` : 'Tell me more about this week';
}

function buildCopy(week: StoredPersonWeek, preferredKeys: readonly string[], firstDayOfWeek = 1): PreviewCardCopy {
  const preAnalyzed = week.analysisStatus === 'ready';
  const buttons = buildButtons(week);
  const opensDraft = buttons.some(button => button.draft);
  const analysis = pickPersonWeekAnalysis(week.analysisByThreshold, preferredKeys);
  // Card prose is week.body; the ✦ link uses the gateway followUpQuestion when present.
  const followUp = preAnalyzed
    ? (analysis?.followUpQuestion?.trim() || followUpQuestion(week, firstDayOfWeek))
    : undefined;
  return {
    title: week.title,
    body: buildPreviewBody(week, firstDayOfWeek),
    buttons,
    followUp,
    preAnalyzed,
    draft: opensDraft
      ? {
        lead: `A note to ${week.userName} about this week.`,
        message: (analysis?.draftMessage ?? week.draftMessage)?.trim() ?? '',
      }
      : undefined,
  };
}

/**
 * Inline daily strip for the company chart week (start day + four), minus today and later days: one
 * bar per fired signal type each of those days, coloured when that signal fired that day, taller on
 * its peak day. `timezone` is the zone "today" is read in (the company's).
 */
export function buildPreviewWeekChart(
  week: StoredPersonWeek, firstDayOfWeek = 1, timezone?: string, now?: DateTime,
): PreviewWeekDay[] {
  const signals = knownSignals(week);
  if (!signals.length) return [];
  const perSignal = signals.map(s => {
    const peak = chartPeak(s, week, firstDayOfWeek);
    return {
      label: isPersonSignalType(s.type) ? signalTypeLabel(s.type) : s.type,
      peakWeekday: peak?.weekday ?? '',
      peakLabel: peak?.display ?? peakLabel(s),
      firedWeekdays: new Set(s.firedDates.map(weekdayLabel)),
    };
  });
  return elapsedChartWeekdays(week.weekStart, firstDayOfWeek, timezone, now).map(day => ({
    day,
    bars: perSignal.map(sig => {
      const breaching = sig.firedWeekdays.has(day);
      const isPeak = day === sig.peakWeekday;
      return {
        label: sig.label,
        breaching,
        isPeak,
        displayValue: isPeak ? sig.peakLabel : (breaching ? 'over limit' : 'under limit'),
      };
    }),
  }));
}

const EMPTY_BODY: RenderedBody = { lines: [], text: '' };

/**
 * One card. Severity/badge/order all come from the stored card; his template reads title/body/
 * buttons off `copy`, so PersonCard.title/body only need to satisfy the type (the detail screen
 * rebuilds its own PersonCard from evidence).
 */
export function buildPersonWeekPreviewCard(
  week: StoredPersonWeek, preferredKeys: readonly string[] = [], firstDayOfWeek = 1, timezone?: string,
): PersonWeekPreviewCard {
  const signals = knownSignals(week);
  const orderedTypes = signals.map(s => s.type as PersonSignalType);
  const severities = {} as Record<PersonSignalType, CardSeverity>;
  signals.forEach(s => { severities[s.type as PersonSignalType] = toCardSeverity(s.severity); });
  const badge = week.severity
    ? toCardSeverity(week.severity)
    : orderedTypes.reduce<CardSeverity>((max, t) => (SEVERITY_RANK[severities[t]] > SEVERITY_RANK[max] ? severities[t] : max), 'info');
  return {
    userId: week.userId,
    userName: week.userName,
    orderedTypes,
    severities,
    badge,
    title: week.title,
    body: { ...EMPTY_BODY, text: week.body },
    signalCount: signals.length,
    // Week id (`…_person_week`) plus each type's id — POST …/signals/status on dismiss/undo.
    signalIds: collectPersonWeekSignalIds(week),
    leadThreshold: signals[0]?.limit.value ?? 0,
    leadUnit: signals[0]?.limit.unit || 'h',
    leadDirection: 'above',
    leadPeak: signals[0]?.peakValue.value ?? 0,
    copy: buildCopy(week, preferredKeys, firstDayOfWeek),
    weekChart: buildPreviewWeekChart(week, firstDayOfWeek, timezone),
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
  };
}

/** Feed order preserved (backend already ranks by manager). */
export function buildPersonWeekPreviewCards(
  weeks: StoredPersonWeek[], preferredKeys: readonly string[] = [], firstDayOfWeek = 1, timezone?: string,
): PersonWeekPreviewCard[] {
  return weeks.map(week => buildPersonWeekPreviewCard(week, preferredKeys, firstDayOfWeek, timezone));
}

// ---- View details evidence -------------------------------------------------------------------

const SECS_PER_HOUR = 3600;

/** Native-unit daily value for one signal type, derived from a day's raw metrics. */
function dailyValue(type: PersonSignalType, metrics: Record<string, number>): number {
  switch (type) {
    case 'idle_time': {
      const trackedMins = (metrics.trackedSec ?? 0) / 60;
      return trackedMins > 0 ? ((metrics.idleMins ?? 0) / trackedMins) * 100 : 0;
    }
    case 'heavy_hours': return (metrics.trackedSec ?? 0) / SECS_PER_HOUR;
    case 'manual_time': return (metrics.manualSec ?? 0) / SECS_PER_HOUR;
    case 'mobile_time': return (metrics.mobileSec ?? 0) / SECS_PER_HOUR;
    case 'unusual_activity': return (metrics.unusualActivityMins ?? 0) / 60;
    default: return 0;
  }
}

/**
 * The gateway's own number for a day that breached, in the same native unit as dailyValue. Preferred
 * over dailyValue for those days so a cell can't disagree with the flag, the header peak or
 * daysOverLimit: dailyValue divides idle by tracked time, which also counts manual time and paid
 * breaks, so it reads lower than the gateway's figure on days that have them. Undefined when the
 * unit isn't the expected one, so the caller falls back to dailyValue.
 */
function issueValue(type: PersonSignalType, issue: StoredPersonWeekDayIssue | undefined): number | undefined {
  const units = type === 'idle_time' ? ['percent', '%'] : ['hours', 'h'];
  return issue && units.includes(issue.value.unit) ? issue.value.value : undefined;
}

/**
 * Band distances the card design uses for View details' per-day chip tint. The response gives a
 * rolled-up severity per signal but not the x/y offsets that produced it, so these are chosen so
 * classifyBaseBand(peak) reproduces that severity. The day matrix's values, over-threshold chips,
 * peak markers and averages don't depend on x/y (only the chip's colour shade does), so an
 * approximation here is safe — see AA-247 plan step 7.
 */
function deriveBandDistances(peak: number, threshold: number, severity: CardSeverity): { x: number, y: number } {
  const gap = Math.max(peak - threshold, Math.max(Math.abs(threshold) * 0.001, 0.001));
  if (severity === 'critical') return { x: gap / 2, y: gap };
  if (severity === 'warning') return { x: gap, y: gap * 3 };
  return { x: gap * 2, y: gap * 3 }; // info: peak stays under threshold + x
}

function toFiredSignal(
  signal: StoredPersonWeekSignal, week: StoredPersonWeek, expectedDays: number, firstDayOfWeek = 1,
): FiredSignal {
  const scoped = chartPeak(signal, week, firstDayOfWeek);
  const threshold = signal.limit.value;
  const peak = scoped?.value ?? signal.peakValue.value;
  const { x, y } = deriveBandDistances(peak, threshold, toCardSeverity(signal.severity));
  const deepLink = signal.deepLink?.trim();
  return {
    type: signal.type as PersonSignalType,
    n: signal.daysOverLimit,
    d: expectedDays,
    threshold,
    peak,
    peakDay: scoped?.weekday ?? '',
    direction: 'above',
    x,
    y,
    ...(deepLink ? { deepLink } : {}),
  };
}

/**
 * Build the per-user week matrix from the stored days/signals. A day with tracked time counts as
 * an evaluated (working) day; a zero-tracking day is treated as not scheduled — the response
 * doesn't carry a schedule, so this is the closest honest read (see AA-247 plan step 7).
 */
export function buildPersonWeekEvidence(week: StoredPersonWeek, firstDayOfWeek = 1): PersonWeekEvidence | undefined {
  const signals = knownSignals(week);
  if (!signals.length || !week.days.length) return undefined;

  const days: PersonWeekDay[] = week.days
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .map(day => {
      const trackedHours = (day.metrics.trackedSec ?? 0) / SECS_PER_HOUR;
      const state: DayState = trackedHours > 0 ? 'evaluated' : 'not_scheduled';
      const values: PersonWeekDayValue[] = signals.map(s => {
        const type = s.type as PersonSignalType;
        const issue = day.issues.find(i => i.type === s.type);
        const href = issue?.deepLink?.trim();
        const fromIssue = issueValue(type, issue);
        const display = fromIssue !== undefined ? issue?.value.display?.trim() : undefined;
        return {
          type,
          value: fromIssue ?? dailyValue(type, day.metrics),
          ...(display ? { display } : {}),
          ...(href ? { href } : {}),
        };
      });
      return { date: day.date, weekday: weekdayLabel(day.date), state, values, trackedHours };
    });

  const workingWeekdays = days.filter(d => d.state === 'evaluated').map(d => d.weekday);
  const expectedDays = Math.max(workingWeekdays.length, 1);

  return {
    userId: week.userId,
    userName: week.userName,
    timezone: week.timezone || 'UTC',
    workingWeekdays,
    days,
    signals: signals.map(s => toFiredSignal(s, week, expectedDays, firstDayOfWeek)),
  };
}

// ---- Follow-up conversation handle -----------------------------------------------------------

/**
 * A minimal live-analysis Signal so the ✦ follow-up can open a real conversation (OpenConversation)
 * scoped to this person — the one place a person-week card calls the LLM. Only the fields the
 * conversation/live-filter plumbing reads carry real data; the rest are inert defaults.
 */
export function personWeekToSignal(
  week: StoredPersonWeek, preferredKeys: readonly string[] = [], firstDayOfWeek = 1,
): Signal {
  const cardSignals = knownSignals(week);
  const lead = cardSignals[0];
  // Same joined label the Needs you person card uses, so History shows one type line for the
  // whole week rather than the first signal's raw id.
  const typeLabels = cardSignals.map(signal => (
    isPersonSignalType(signal.type) ? signalTypeLabel(signal.type) : signal.type
  ));
  // Card body stays week.body. Chat opens with followUpQuestion / followUpAnswer.
  const analysis = pickPersonWeekAnalysis(week.analysisByThreshold, preferredKeys);
  const askPrompt = analysis?.followUpQuestion?.trim() || followUpQuestion(week, firstDayOfWeek);
  const followUpAnswer = (analysis?.followUpAnswer ?? week.followUpAnswer)?.trim() || '';
  const draftMessage = (analysis?.draftMessage ?? week.draftMessage)?.trim() ?? '';
  const body = week.body;
  const answer = followUpAnswer || body;
  return {
    id: week.userId,
    key: lead?.type ?? 'idle_time',
    type: joinWithAnd(typeLabels) || (lead?.type ?? 'idle_time'),
    ...(typeLabels.length ? { types: typeLabels } : {}),
    sev: toCardSeverity(week.severity),
    signalIds: collectPersonWeekSignalIds(week),
    weekStart: week.weekStart,
    weekEnd: week.weekEnd,
    title: week.title,
    body,
    parts: [{ text: body }],
    answerParts: [{ text: answer }],
    answer,
    trend: false,
    chartCaption: '',
    bars: [],
    userCount: 1,
    userName: week.userName,
    userBars: [{ id: week.userId, name: week.userName, value: lead?.peakValue.value ?? 0 }],
    threshold: lead?.limit.value ?? 0,
    unit: lead?.limit.unit ?? '',
    direction: 'above',
    askPrompt,
    recommendation: '',
    draftMessage,
    followUps: [],
    reportName: '',
    reportUrl: '',
    chipContext: `${week.userName}, this week`,
    evidenceCount: cardSignals.length || 1,
    period: '',
    metricLabel: '',
    evBody: '',
    rows: [],
  };
}

/**
 * Live-analysis handles, one per card. Analyzed cards use theirs from the ✦ follow-up; unanalyzed
 * cards use theirs from ANALYZE, which is the only way they call the LLM (AA-247 plan step 10).
 */
export function personWeekSignals(
  weeks: StoredPersonWeek[], preferredKeys: readonly string[] = [], firstDayOfWeek = 1,
): Signal[] {
  return weeks.map(week => personWeekToSignal(week, preferredKeys, firstDayOfWeek));
}
